import { InputAmount, MMButton } from '@components'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useContext, useState } from '@integrations/react'
import {
  AccountTypes,
  selectMerchantCurrency,
  useAppSelector,
  useMerchantAccounts,
  useSetExpenseLimitMutation
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { fixFloat, GlobalContext, getCurrencySymbol, tr } from 'mmfintech-commons'
import { RequestModalBody, RequestModalSurface, RequestModalTitle } from '../RequestModalSurface'
import styles from './ChangeLimitModal.module.scss'

interface Props {
  merchantUserId: number
  merchantUserName: string
  currency: string
  monthlyVolume: number
}

function ChangeLimitModal({ merchantUserId, merchantUserName, currency, monthlyVolume }: Props) {
  const { modalHide } = useContext(GlobalContext)

  const merchantCurrency = useAppSelector(selectMerchantCurrency)

  const { activeAccounts } = useMerchantAccounts()

  const [changeLimit] = useSetExpenseLimitMutation()
  const [updatedMonthlyVolume, setUpdatedMonthlyVolume] = useState(monthlyVolume)

  function onSuccess() {
    modalHide()
    toast.success(tr('FRONTEND.LIMITS.CHANGE_LIMIT.SUCCESS_TOAST', 'Limit successfully updated'))
  }

  async function handleSubmit() {
    if (updatedMonthlyVolume) {
      try {
        await changeLimit({
          monthlyVolume: fixFloat(updatedMonthlyVolume),
          merchantUserId
        }).unwrap()
        onSuccess()
      } catch (_error) {}
    }
  }

  return (
    <RequestModalSurface
      className={cn(styles['change-limit-modal-wrapper'], 'change-limit-modal-wrapper')}
      data-test='change-limit-modal'
    >
      <div className={cn(styles['change-limit-inner'], 'change-limit-inner')}>
        <RequestModalTitle className={cn('title')}>
          {tr('FRONTEND.LIMITS.CHANGE_LIMIT.TITLE', 'Change limit')}
        </RequestModalTitle>
        <RequestModalBody className={cn(styles['change-limit-text'], 'change-limit-text')}>
          {tr('FRONTEND.LIMITS.CHANGE_LIMIT.BODY', 'Change the monthly limit of user:')} <span>{merchantUserName}</span>
        </RequestModalBody>

        <InputAmount
          label={tr('FRONTEND.LIMITS.CHANGE_LIMIT.AMOUNT', 'Set monthly limit')}
          placeholder={getCurrencySymbol(currency)}
          amount={updatedMonthlyVolume.toString()}
          setAmount={(value) => setUpdatedMonthlyVolume(parseFloat(value))}
          account={activeAccounts?.find(
            (acc: AccountBalanceResponse) => acc.currencyCode === merchantCurrency && acc.type === AccountTypes.PRIMARY
          )}
        />

        <MMButton
          variant='primary'
          caption={tr('FRONTEND.LIMITS.CHANGE_LIMIT.BUTTON_SET', 'Set')}
          onClick={handleSubmit}
          disabled={!updatedMonthlyVolume}
          className={cn('mb-1 mt-2')}
          fullWidth
          data-test='set-button'
        />
        <MMButton
          variant='secondary'
          caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={modalHide}
          fullWidth
          data-test='cancel-button'
        />
      </div>
    </RequestModalSurface>
  )
}

export default ChangeLimitModal
